import { useEffect, useRef } from 'react';
import type { Track } from 'livekit-client';
import { Icon } from '../ui/icons';
import type { CallParticipantView } from './CallProvider';
import { ModerationMenu } from './ModerationMenu';
import { RoleBadge } from './RoleBadge';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Video is attached imperatively (`track.attach`) rather than through a
 * `srcObject` prop: livekit-client owns the media stream's lifecycle, and a
 * `<video>` element is not something React's diffing should be reconciling.
 */
function useAttachedVideo(track: Track | null, enabled: boolean) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track || !enabled) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track, enabled]);
  return ref;
}

/**
 * Face of the person being followed — their live camera when they are on
 * the call with it on, otherwise the same coloured disc as their cursor.
 */
export function FollowFace({
  participant,
  name,
  color,
}: {
  participant: CallParticipantView | null;
  name: string;
  color: string;
}) {
  const showVideo = Boolean(participant?.camOn && participant.cameraTrack);
  const videoRef = useAttachedVideo(participant?.cameraTrack ?? null, showVideo);

  return (
    <span className="follow-pip" style={{ ['--tile-color' as string]: color }}>
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`follow-pip-video ${participant?.mirrorCamera ? 'is-mirrored' : ''}`}
        />
      ) : (
        <span className="follow-pip-disc" style={{ background: color }}>
          {initials(name)}
        </span>
      )}
    </span>
  );
}

/**
 * A rectangular video tile — the call's main unit, used in the grid, on the
 * expanded stage and in the filmstrip under it. A tile is always the same
 * person as the coloured cursor on the canvas: the fallback disc uses the
 * presence colour, so a face on the call and a cursor on the board read as
 * one participant.
 *
 * When the camera is off but they are sharing a screen, the share fills the
 * tile — otherwise the disc would hide the only video they have on.
 */
export function ParticipantTile({
  participant,
  variant = 'grid',
  focused = false,
  onSelect,
}: {
  participant: CallParticipantView;
  variant?: 'grid' | 'stage' | 'strip';
  focused?: boolean;
  onSelect?: () => void;
}) {
  const showCamera = participant.camOn && participant.cameraTrack !== null;
  const showScreen = !showCamera && participant.screenShareTrack !== null;
  const track = showCamera
    ? participant.cameraTrack
    : showScreen
      ? participant.screenShareTrack
      : null;
  const videoRef = useAttachedVideo(track, Boolean(track));

  return (
    <button
      type="button"
      className={`call-tile call-tile-${variant} ${participant.speaking ? 'is-speaking' : ''} ${
        focused ? 'is-focused' : ''
      } ${showScreen ? 'is-screen' : ''}`}
      style={{ ['--tile-color' as string]: participant.color }}
      onClick={onSelect}
      aria-label={`${participant.name}${participant.isLocal ? ' (вы)' : ''}${
        participant.role ? `, ${participant.role.label}` : ''
      }${showScreen ? ', демонстрация экрана' : ''}`}
      aria-pressed={focused}
    >
      {track ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`call-tile-video ${showCamera && participant.mirrorCamera ? 'is-mirrored' : ''} ${
            showScreen ? 'is-screen' : ''
          }`}
        />
      ) : (
        <span className="call-tile-disc" style={{ background: participant.color }}>
          {initials(participant.name)}
        </span>
      )}

      <span className="call-tile-label">
        {!participant.micOn && (
          <span className="call-tile-muted" aria-hidden="true">
            <Icon name="micOff" size={11} />
          </span>
        )}
        <span className="call-tile-name">
          {participant.name}
          {participant.isLocal ? ' (вы)' : ''}
        </span>
        <RoleBadge role={participant.role} small />
        {!participant.isLocal && (
          <span className="call-tile-mod" onClick={(e) => e.stopPropagation()}>
            <ModerationMenu userId={participant.identity} userName={participant.name} />
          </span>
        )}
      </span>
    </button>
  );
}

/** Overlapping circular avatars for the collapsed pill. */
export function ParticipantChips({ participants }: { participants: CallParticipantView[] }) {
  return (
    <div className="call-chips">
      {participants.slice(0, 5).map((p) => (
        <ParticipantChip key={p.identity} participant={p} />
      ))}
      {participants.length > 5 && <span className="call-chip call-chip-more">+{participants.length - 5}</span>}
    </div>
  );
}

function ParticipantChip({ participant }: { participant: CallParticipantView }) {
  const showVideo = participant.camOn && participant.cameraTrack !== null;
  const videoRef = useAttachedVideo(participant.cameraTrack, showVideo);

  return (
    <span
      className={`call-chip ${participant.speaking ? 'is-speaking' : ''}`}
      style={{ ['--tile-color' as string]: participant.color }}
      data-tip={`${participant.name}${participant.isLocal ? ' · вы' : ''}${
        participant.role ? ` · ${participant.role.label}` : ''
      }${participant.micOn ? '' : ' · микрофон выключен'}`}
    >
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`call-chip-video ${participant.mirrorCamera ? 'is-mirrored' : ''}`}
        />
      ) : (
        <span className="call-chip-disc" style={{ background: participant.color }}>
          {initials(participant.name)}
        </span>
      )}
      {!participant.micOn && (
        <span className="call-chip-muted" aria-hidden="true">
          <Icon name="micOff" size={9} />
        </span>
      )}
    </span>
  );
}
